import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/Table';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Select } from '../components/ui/Select';
import { Checkbox } from '../components/ui/Checkbox';
import { StatusToggle } from '../components/ui/StatusToggle';
import { MOCK_BRANCHES } from '../utils/mockData';
import { Plus, Search, Building2, FileText, Clock, Hash, Edit, ChevronLeft, ChevronRight } from 'lucide-react';
import { Branch } from '../types';
const ITEMS_PER_PAGE = 10;
export function Branches() {
  const [branches, setBranches] = useState<Branch[]>(MOCK_BRANCHES);
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const handleSearch = (val: string) => {
    setSearchQuery(val);
    setCurrentPage(1);
  };
  const handleCityFilter = (val: string) => {
    setCityFilter(val);
    setCurrentPage(1);
  };
  const filteredBranches = branches.filter(branch => {
    const query = searchQuery.toLowerCase();
    const matchesSearch = branch.name.toLowerCase().includes(query) || branch.city.toLowerCase().includes(query) || branch.code.toLowerCase().includes(query);
    const matchesCity = cityFilter === 'all' || branch.city === cityFilter;
    return matchesSearch && matchesCity;
  });
  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredBranches.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedBranches = filteredBranches.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);
  const startRow = (safePage - 1) * ITEMS_PER_PAGE + 1;
  const endRow = Math.min(safePage * ITEMS_PER_PAGE, filteredBranches.length);
  const branchCities = [...new Set(branches.map(b => b.city))].sort();
  return <div className="space-y-6 pb-12" data-id="element-426">
      <div className="flex items-center justify-between" data-id="element-427">
        <div data-id="element-428">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-429">Branches</h1>
          <p className="text-slate-500" data-id="element-430">
            Manage your pharmacy branches and locations
          </p>
        </div>
        <Button onClick={() => setIsAddModalOpen(true)} data-id="element-431">
          <Plus className="w-4 h-4 mr-2" data-id="element-432" />
          Add Branch
        </Button>
      </div>

      <Card data-id="element-433">
        <CardHeader className="pb-3" data-id="element-434">
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-435">
            <div className="relative flex-1" data-id="element-436">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-437" />
              <Input placeholder="Search branches by name, city, or code..." value={searchQuery} onChange={e => handleSearch(e.target.value)} className="pl-9" data-id="element-438" />
            </div>
            <div className="w-full sm:w-48" data-id="element-439">
              <Select value={cityFilter} onChange={e => handleCityFilter(e.target.value)} options={[{
              value: 'all',
              label: 'All Cities'
            }, ...branchCities.map(city => ({
              value: city,
              label: city
            }))]} data-id="element-440" />
            </div>
          </div>
        </CardHeader>
        <CardContent data-id="element-441">
          <div className="rounded-md border border-slate-200 overflow-hidden shadow-sm" data-id="element-442">
            <Table data-id="element-443">
              <TableHeader data-id="element-444">
                <TableRow data-id="element-445">
                  <TableHead data-id="element-446">Branch Name</TableHead>
                  <TableHead data-id="element-447">Code</TableHead>
                  <TableHead data-id="element-448">City</TableHead>
                  <TableHead data-id="element-449">Email</TableHead>
                  <TableHead data-id="element-450">Status</TableHead>
                  <TableHead className="text-right" data-id="element-451">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-452">
                {paginatedBranches.length > 0 ? paginatedBranches.map(branch => <TableRow key={branch.id} className="hover:bg-slate-50" data-id="element-453">
                      <TableCell className="font-medium text-slate-900" data-id="element-454">
                        {branch.name}
                      </TableCell>
                      <TableCell data-id="element-455">
                        <div className="flex items-center gap-2 font-mono text-xs text-slate-600" data-id="element-456">
                          <Hash className="w-3 h-3 text-slate-400" data-id="element-457" />
                          {branch.code}
                        </div>
                      </TableCell>
                      <TableCell data-id="element-458">
                        <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-459">
                          <Building2 className="w-3 h-3 text-slate-400" data-id="element-460" />
                          {branch.city}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600" data-id="element-461">
                        {branch.email || '—'}
                      </TableCell>
                      <TableCell data-id="element-462">
                        <StatusToggle checked={branch.status === 'ACTIVE'} onChange={checked => {
                    setBranches(prev => prev.map(b => b.id === branch.id ? {
                      ...b,
                      status: checked ? 'ACTIVE' : 'INACTIVE'
                    } : b));
                  }} data-id="element-463" />
                      </TableCell>
                      <TableCell className="text-right" data-id="element-464">
                        <Button variant="ghost" size="sm" onClick={() => {}} data-id="element-465">
                          <Edit className="w-4 h-4 mr-2" data-id="element-466" />
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>) : <TableRow data-id="element-467">
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500" data-id="element-468">
                      No branches found matching your search.
                    </TableCell>
                  </TableRow>}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {filteredBranches.length > 0 && <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100" data-id="element-469">
              <p className="text-sm text-slate-500" data-id="element-470">
                Showing{' '}
                <span className="font-medium text-slate-700" data-id="element-471">{startRow}</span>{' '}
                to <span className="font-medium text-slate-700" data-id="element-472">{endRow}</span>{' '}
                of{' '}
                <span className="font-medium text-slate-700" data-id="element-473">
                  {filteredBranches.length}
                </span>{' '}
                rows
              </p>
              <div className="flex items-center gap-2" data-id="element-474">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage <= 1} data-id="element-475">
                  <ChevronLeft className="w-4 h-4 mr-1" data-id="element-476" />
                  Previous
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} data-id="element-477">
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" data-id="element-478" />
                </Button>
              </div>
            </div>}
        </CardContent>
      </Card>

      {/* Add Branch Modal */}
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add New Branch" data-id="element-479">
        <div className="space-y-4" data-id="element-480">
          <Input label="Branch Name" placeholder="e.g. Downtown Riyadh" data-id="element-481" />
          <div className="grid grid-cols-2 gap-4" data-id="element-482">
            <Input label="Branch Code" placeholder="e.g. PH-RYD-001" data-id="element-483" />
            <Input label="City" placeholder="e.g. Riyadh" data-id="element-484" />
          </div>
          <Input label="Email Address" placeholder="branch@pharmacy.com" data-id="element-485" />
          <div className="flex justify-end gap-3 pt-4" data-id="element-486">
            <Button variant="outline" onClick={() => setIsAddModalOpen(false)} data-id="element-487">
              Cancel
            </Button>
            <Button variant="primary" data-id="element-488">Add Branch</Button>
          </div>
        </div>
      </Modal>
    </div>;
}